import React from 'react';
import { Theme, withStyles, WithStyles } from '@material-ui/core';
import { InjectedIntlProps, injectIntl } from 'react-intl';
import createStyles from '@material-ui/core/styles/createStyles';
import Typography from '@material-ui/core/Typography';
import Slide from '@material-ui/core/Slide';
import Paper from '@material-ui/core/Paper';
// @ts-ignore
import ReactTable from 'react-table';
// @ts-ignore
import CopyToClipboardChip from 'hcla-web-frontend-primitives/app/components/primitives/Chips/CopyToClipboardChip';
import { QuantificationResultJSON } from '../Quantification/QuantificationResultJSON';
import { QCTypeParams } from '../Quantification/QuantificationConfigurationJSON';
import UncertainProbabilityChip from './Chips/UncertainProbabilityChip';
import { NodeLabel } from './NodeLabel/NodeLabel';


const styles = (theme: Theme) => createStyles({
  root: {
    height: '100%',
    margin: '0',
    overflow: 'auto',
    padding: '0',
    fontFamily: theme.typography.fontFamily,
    color: theme.palette.text.primary,
  },
  probContainer: {
    marginBottom: theme.spacing(2),
  },
  tableContainer: {
    height: '100%',
    marginTop: '0',
  },
  labelProb: {
    fontSize: '2rem',
    userSelect: 'all',
  },
  rootProb: {
    padding: '24px 0px',
  },
  cutsetChip: {
    margin: `0px ${theme.spacing(0.5)}px`,
    flexDirection: 'row',
  },
  cutsetRowItem: {
    flexDirection: 'row',
  },
  cutsetRow: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  rowItem: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  uncertainRow: {
    display: 'flex',
    flexDirection: 'row',
  },
});

interface Props extends WithStyles<typeof styles>, InjectedIntlProps {
  noSlide: boolean;
  loading: boolean;
  data: QuantificationResultJSON;
  type: QCTypeParams;
  title?: string;
  loadingText?: string;
  noDataText?: string;
}

const BayesianNetworkResultView = (props: Props) => {
  const {
    intl, classes, data, data: { result_data: { label_map } }, title, loading, loadingText, noDataText, noSlide, type, ...others
  } = props;

  // filter out label map from table data
  const inputData = Object.entries(data.result_data).filter(entry => entry[0] != 'label_map');

  const styled = (item: JSX.Element) => {
    return <div className={classes.rowItem}>{item}</div>;
  };

  // use an uncertain or point estimate accessor function based on quantification type
  let acc;
  if (type == QCTypeParams.POINT_ESTIMATE) {
    acc = (row: [string, any]) => (
      <div className={classes.cutsetRow}>
        <div className={classes.cutsetRowItem}>
          {
            Object.entries(row[1]).map(entry => <CopyToClipboardChip className={classes.cutsetChip} color="default" variant="default" label={`${entry[0]}: ${Number(entry[1]).toExponential(3)}`} />)
          }
        </div>
      </div>
    );
  } else {
    acc = (row: [string, any]) => (
      <div className={classes.uncertainRow}>
        {
          Object.entries(row[1]).map(entry => <UncertainProbabilityChip name={`NODE_${row[0]}.${treeID}`} data={entry[1]} map={label_map} label={entry[0]} classes={classes} className={classes.cutsetChip} />)
        }
      </div>
    );
  }

  const treeID = data.configuration.constructor.tree_id;
  const columns = [
    {
      id: 'id',
      maxWidth: 120,
      Header: <Typography align="center" variant="h6">{intl.formatMessage({ id: 'BayesianNetworkResultView.Columns.Header.id' })}</Typography>,
      accessor: (row: [string, any]) => (styled(<Typography align="center" variant="subtitle1">{NodeLabel.buildWithDefaults(label_map, `NODE_${row[0]}.${treeID}`).label.name}</Typography>)),
    },
    {
      id: 'probability',
      // maxWidth: 120,
      Header: <Typography align="center" variant="h6">{intl.formatMessage({ id: 'BayesianNetworkResultView.Columns.Header.probability' })}</Typography>,
      accessor: acc,
    },
  ];

  const isDataEmpty = () => {
    return data === undefined || data === null || Object.keys(data).length === 0 || data.result_data === null || Object.keys(data.result_data).length === 0;
  };

  if (isDataEmpty()) {
    return (
      <Slide direction="up" in mountOnEnter unmountOnExit>
        <Paper className={classes.root} {...others} square elevation={0}>
          { noDataText }
        </Paper>
      </Slide>
    );
  }


  let content;
  if (loading || isDataEmpty()) {
    content = loadingText;
  } else {
    content = (
      <React.Fragment>
        { title }
        <div className={classes.tableContainer}>
          <ReactTable
            // @ts-ignore
            data={inputData}
            noDataText={intl.formatMessage({ id: 'BayesianNetworkResultView.table.no_result' })}
            columns={columns}
            defaultPageSize={10}
            style={{ height: '100%' }}
            className="-striped -highlight"
          />
        </div>
      </React.Fragment>
    );
  }
  const wrapper = <Paper className={classes.root} {...others} square elevation={0}>{content}</Paper>;
  if (noSlide) {
    return wrapper;
  }
  return (
    <Slide direction="up" in mountOnEnter unmountOnExit>
      {wrapper}
    </Slide>
  );
};

export default withStyles(styles)(injectIntl(BayesianNetworkResultView));
